import React, {useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {QUERY_CACHE_KEYS} from '../utils/constants';
import {useChekinSDK} from '../context/ChekinSDK';
import {LocationType, SelectOptionType} from '@guestapp/sdk';

function getLocationsAsOptions(locations: Array<LocationType>): SelectOptionType[] {
  if (!locations) {
    return [];
  }

  return locations.map(l => {
    return {
      label: l?.country?.name,
      value: l?.country?.code,
    };
  });
}

type UseLocationsOptions = {
  enabled?: boolean;
};

function useFetchLocations(props?: UseLocationsOptions) {
  const {i18n} = useTranslation();
  const {getAllCountries} = useChekinSDK();

  const {
    data: locations,
    isError,
    error,
    isLoading: isLoadingLocations,
  } = useQuery([QUERY_CACHE_KEYS.locations, i18n.language], () => getAllCountries(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...props,
  });

  useEffect(() => {
    if (error) {
      toast.error('Requested counties list could not be found. Please contact support.', {
        theme: 'colored',
      });
    }
  }, [error]);

  const locationsAsOptions = React.useMemo(() => {
    if (locations) {
      return getLocationsAsOptions(locations);
    }

    return [];
  }, [locations]);

  const phoneCodesAsOptions = React.useMemo(() => {
    if (locations) {
      return locations?.map(l => {
        const phoneCodeValue = `+${l.phone_code}`;

        return {
          value: phoneCodeValue,
          label: `${l?.country?.name} ${phoneCodeValue}`,
        };
      });
    }

    return [];
  }, [locations]);

  return {
    locations,
    isError,
    isLoadingLocations,
    error,
    locationsAsOptions,
    phoneCodesAsOptions,
  };
}

export {useFetchLocations};
